import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Eye, EyeOff } from 'lucide-react';
import AllSportsHubsLogo from '../pages/AllSportsHubs.png';

const ResetPassword = () => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isValidToken, setIsValidToken] = useState(false);
  const [checkingToken, setCheckingToken] = useState(true);
  
  const navigate = useNavigate();

  useEffect(() => {
    // Check if we have a valid session from the password reset link
    const checkSession = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error || !session) {
          console.error('No valid session:', error);
          setMessage({ 
            type: 'error', 
            text: 'Invalid or expired reset link. Please request a new password reset.' 
          });
          setIsValidToken(false);
        } else {
          console.log('Valid reset session found');
          setIsValidToken(true);
        }
      } catch (err) {
        console.error('Session check error:', err);
        setMessage({ 
          type: 'error', 
          text: 'An error occurred. Please try again.' 
        });
        setIsValidToken(false);
      } finally {
        setCheckingToken(false);
      }
    };

    checkSession();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    // Validation
    if (newPassword !== confirmPassword) {
      setMessage({ type: 'error', text: 'Passwords do not match' });
      return;
    }

    if (newPassword.length < 8) {
      setMessage({ type: 'error', text: 'Password must be at least 8 characters long' });
      return;
    }

    setLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) {
        throw error;
      }

      setMessage({ 
        type: 'success', 
        text: 'Password reset successfully! Redirecting to login...' 
      });

      // Sign out and redirect to login after 2 seconds
      setTimeout(async () => {
        await supabase.auth.signOut();
        navigate('/login', { 
          state: { message: 'Password reset successful. Please log in with your new password.' }
        });
      }, 2000);

    } catch (error) {
      console.error('Password reset error:', error);
      setMessage({ 
        type: 'error', 
        text: error.message || 'Failed to reset password. Please try again.' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLogoClick = () => {
    navigate('/');
  };

  if (checkingToken) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        flexDirection: 'column',
        gap: '1rem'
      }}>
        <div style={{ 
          width: '50px', 
          height: '50px', 
          border: '4px solid #f3f3f3',
          borderTop: '4px solid #1e74c7',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <p style={{ color: '#666' }}>Verifying reset link...</p>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (!isValidToken) {
    return (
      <div className="auth-container" style={{ maxWidth: 420, margin: '0 auto', padding: '2rem' }}>
        <div 
          style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            marginBottom: '1.5rem',
            cursor: 'pointer'
          }}
          onClick={handleLogoClick}
        >
          <img 
            src={AllSportsHubsLogo} 
            alt="All Sports Hubs Logo" 
            style={{ height: '125px', width: 'auto', objectFit: 'contain' }} 
          />
        </div>

        <div style={{
          backgroundColor: '#fee',
          border: '1px solid #fcc',
          borderRadius: '8px',
          padding: '1rem',
          marginBottom: '1.5rem'
        }}>
          <h3 style={{ margin: '0 0 0.5rem 0', color: '#c33' }}>Invalid Reset Link</h3>
          <p style={{ margin: 0, color: '#666' }}>
            This password reset link is invalid or has expired. Please request a new one.
          </p>
        </div>

        <button
          onClick={() => navigate('/login')}
          style={{
            width: '100%',
            padding: '0.8rem',
            fontSize: '1rem',
            borderRadius: 28,
            background: 'linear-gradient(90deg,#1e74c7,#7fc3f2)',
            color: 'white',
            border: 'none',
            cursor: 'pointer',
            boxShadow: '0 6px 18px rgba(30,116,199,0.18)'
          }}
        >
          Back to Login
        </button>
      </div>
    );
  }

  return (
    <div className="auth-container animate-fade-in-up" style={{ maxWidth: 420, margin: '0 auto', padding: '2rem' }}>
      <div 
        style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          marginBottom: '1.5rem',
          cursor: 'pointer'
        }}
        onClick={handleLogoClick}
      >
        <img 
          src={AllSportsHubsLogo} 
          alt="All Sports Hubs Logo" 
          style={{ height: '125px', width: 'auto', objectFit: 'contain' }} 
        />
      </div>

      <h2 style={{ marginBottom: '0.5rem', color: '#1e74c7', textAlign: 'center' }}>
        Reset Your Password
      </h2>
      <p style={{ marginBottom: '1.5rem', color: '#666', textAlign: 'center', fontSize: '0.9rem' }}>
        Enter your new password below
      </p>

      {message.text && (
        <div style={{ 
          padding: '10px', 
          marginBottom: '15px',
          backgroundColor: message.type === 'success' ? '#d4edda' : '#f8d7da',
          color: message.type === 'success' ? '#155724' : '#721c24',
          border: '1px solid',
          borderColor: message.type === 'success' ? '#c3e6cb' : '#f5c6cb',
          borderRadius: '4px'
        }}>
          {message.text}
        </div>
      )}

      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column' }}>
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', color: '#2c3e50', fontSize: '0.9rem' }}>
            New Password
          </label>
          <div style={{ position: 'relative' }}>
            <input
              type={showPassword ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={8}
              disabled={loading}
              style={{ 
                width: '100%',
                fontSize: '1rem',
                padding: '12px',
                paddingRight: '40px',
                borderRadius: '8px',
                border: '1px solid #cfe9ff',
                opacity: loading ? 0.6 : 1,
                boxSizing: 'border-box'
              }}
              placeholder="Enter new password"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              disabled={loading}
              style={{
                position: 'absolute',
                right: '10px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                cursor: loading ? 'not-allowed' : 'pointer',
                color: '#666',
                padding: '5px',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', color: '#2c3e50', fontSize: '0.9rem' }}>
            Confirm New Password
          </label>
          <div style={{ position: 'relative' }}>
            <input
              type={showConfirmPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={8}
              disabled={loading}
              style={{ 
                width: '100%',
                fontSize: '1rem',
                padding: '12px',
                paddingRight: '40px',
                borderRadius: '8px',
                border: '1px solid #cfe9ff',
                opacity: loading ? 0.6 : 1,
                boxSizing: 'border-box'
              }}
              placeholder="Confirm new password"
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              disabled={loading}
              style={{
                position: 'absolute',
                right: '10px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                cursor: loading ? 'not-allowed' : 'pointer',
                color: '#666',
                padding: '5px',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>

        <button 
          type="submit" 
          disabled={loading}
          style={{ 
            padding: '0.8rem', 
            fontSize: '1.1rem',
            borderRadius: 28,
            background: loading ? '#ccc' : 'linear-gradient(90deg,#1e74c7,#7fc3f2)',
            color: 'white',
            border: 'none',
            boxShadow: '0 6px 18px rgba(30,116,199,0.18)',
            cursor: loading ? 'not-allowed' : 'pointer'
          }}
        >
          {loading ? 'Resetting Password...' : 'Reset Password'}
        </button>
      </form>

      <p style={{ marginTop: '1.5rem', color: '#2c3e50', textAlign: 'center' }}>
        Remember your password?{' '}
        <span
          onClick={() => !loading && navigate('/login')}
          style={{ 
            cursor: loading ? 'not-allowed' : 'pointer',
            color: '#1e74c7',
            textDecoration: 'underline'
          }}
        >
          Back to Login
        </span>
      </p>
    </div>
  );
};

export default ResetPassword;